// contacts.js — Phase 3.5: Contacts Page
// Contact list · Role filters · Detail panel · David Intelligence query bar

import { apiFetch, formatDate } from './utils.js';
import { state } from './state.js';

let davidExpanded = false;
let selectedContactId = null;

export function initContacts() {
  window.renderContactsView = renderContactsView;
}

export async function renderContactsView() {
  const container = document.getElementById('view-contacts');
  if (!container) return;

  const contacts = await apiFetch('/api/contacts').catch(() => []);
  state.contacts = contacts;

  const roles = [...new Set(contacts.map(c => c.role).filter(Boolean))].sort();

  container.innerHTML = `
    <div class="contacts-v4">
      ${renderDavidQueryBar()}
      <div class="contacts-body">
        <div class="contact-list-panel">
          <div class="contact-list-header">
            <div class="role-filter-chips">
              <button class="role-chip active" onclick="filterContacts('all', this)">All</button>
              ${roles.map(r => `<button class="role-chip" onclick="filterContacts('${escAttr(r)}', this)">${r}</button>`).join('')}
            </div>
          </div>
          <div class="contact-list" id="contact-list">
            ${contacts.length === 0
              ? '<div class="empty-state">No contacts yet</div>'
              : contacts.map(c => renderContactRow(c)).join('')}
          </div>
        </div>
        <div class="contact-detail-v4" id="contact-detail-v4">
          <div class="detail-placeholder">Select a contact</div>
        </div>
      </div>
    </div>
  `;

  setupContactsHandlers();
}

function renderDavidQueryBar() {
  return `
    <div class="david-query-section">
      <div class="david-query-header" onclick="toggleDavidQuery()">
        <span class="david-indicator">✦ David Intelligence</span>
        <span class="david-toggle-hint" id="david-toggle-hint">${davidExpanded ? 'collapse' : 'expand'}</span>
      </div>
      <div class="david-query-body ${davidExpanded ? '' : 'collapsed'}" id="david-query-body">
        <div class="david-query-input-row">
          <input type="text" id="david-query-input" class="david-query-input"
                 placeholder="Who have I had percussion success with?">
          <button onclick="runDavidQuery()" class="btn-david-query">Ask David</button>
        </div>
        <div class="david-query-response" id="david-query-response"></div>
      </div>
    </div>
  `;
}

function renderContactRow(contact) {
  return `
    <div class="contact-row ${contact.id === selectedContactId ? 'selected' : ''}"
         data-id="${contact.id}"
         data-role="${escAttr(contact.role || '')}"
         onclick="selectContact(${contact.id})">
      <div class="contact-row-name">${contact.name}</div>
      <div class="contact-row-meta">
        ${contact.role ? `<span class="contact-row-role">${contact.role}</span>` : ''}
        ${contact.client_count > 0 ? `<span class="contact-row-clients">${contact.client_count} client${contact.client_count != 1 ? 's' : ''}</span>` : ''}
      </div>
    </div>
  `;
}

function renderContactDetail(contact, history) {
  const { show_history = [], competition_results = [] } = history;
  const allAwards = competition_results.flatMap(r => {
    try { return Array.isArray(r.caption_awards) ? r.caption_awards : JSON.parse(r.caption_awards || '[]'); }
    catch { return []; }
  });

  return `
    <div class="contact-detail-content">
      <div class="contact-detail-head">
        <div class="contact-detail-name">${contact.name}</div>
        ${contact.role  ? `<div class="contact-detail-role">${contact.role}</div>` : ''}
        ${contact.email ? `<div class="contact-detail-email"><a href="mailto:${contact.email}">${contact.email}</a></div>` : ''}
        ${contact.phone ? `<div class="contact-detail-phone">${contact.phone}</div>` : ''}
      </div>

      <div class="contact-stats-row">
        <div class="contact-stat">
          <span class="stat-num">${show_history.length}</span>
          <span class="stat-label">shows</span>
        </div>
        <div class="contact-stat">
          <span class="stat-num">${allAwards.length}</span>
          <span class="stat-label">caption awards</span>
        </div>
        <div class="contact-stat">
          <span class="stat-num">${new Set(show_history.map(s => s.client_id)).size}</span>
          <span class="stat-label">clients</span>
        </div>
      </div>

      ${show_history.length > 0 ? `
        <div class="show-history-section">
          <div class="section-label">Show History</div>
          ${show_history.map(sh => {
            const results = competition_results.filter(r => r.show_name === sh.show_name && r.client_name === sh.client_name);
            return `
              <div class="show-history-item">
                <div class="sh-header">
                  <span class="sh-show">${sh.show_name || '—'}</span>
                  <span class="sh-client">${sh.client_name}</span>
                  ${sh.season_year ? `<span class="sh-season">${sh.season_year}</span>` : ''}
                </div>
                ${results.length > 0 ? `
                  <div class="sh-results">
                    ${results.slice(0, 3).map(r => {
                      let awards = [];
                      try { awards = Array.isArray(r.caption_awards) ? r.caption_awards : JSON.parse(r.caption_awards || '[]'); } catch {}
                      return `<div class="sh-result-item">
                        ${r.event_name}${r.placement ? ` — ${r.placement} place` : ''}${awards.length > 0 ? ` · ${awards.join(', ')}` : ''}
                      </div>`;
                    }).join('')}
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>
      ` : ''}

      <div class="contact-notes-section">
        <div class="section-label">Notes</div>
        <textarea class="contact-notes-area" placeholder="Add notes…"
                  onblur="saveContactNotes(${contact.id}, this.value)">${contact.notes || ''}</textarea>
      </div>
    </div>
  `;
}

function setupContactsHandlers() {
  document.getElementById('david-query-input')?.addEventListener('keydown', e => {
    if (e.key === 'Enter') window.runDavidQuery();
  });
}

function escAttr(str) {
  return String(str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// ── Window-level handlers ──────────────────────────────────────────────────

window.toggleDavidQuery = function() {
  davidExpanded = !davidExpanded;
  const body = document.getElementById('david-query-body');
  const hint = document.getElementById('david-toggle-hint');
  if (body) body.classList.toggle('collapsed', !davidExpanded);
  if (hint) hint.textContent = davidExpanded ? 'collapse' : 'expand';
};

window.runDavidQuery = async function() {
  const input = document.getElementById('david-query-input');
  const responseEl = document.getElementById('david-query-response');
  if (!input || !responseEl) return;
  const query = input.value.trim();
  if (!query) return;

  responseEl.innerHTML = '<div class="david-thinking">David is thinking…</div>';
  try {
    const result = await apiFetch('/api/contacts/david-query', {
      method: 'POST',
      body: JSON.stringify({ query })
    });
    responseEl.innerHTML = `
      <div class="david-response">
        <div class="david-response-text">${result.response}</div>
        ${result.contacts?.length > 0 ? `
          <div class="david-response-contacts">
            ${result.contacts.map(c => `
              <button class="david-contact-suggestion" onclick="selectContact(${c.id})">${c.name}</button>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `;
  } catch (err) {
    responseEl.innerHTML = '<div class="david-error">Query failed</div>';
  }
};

window.selectContact = async function(id) {
  selectedContactId = id;
  document.querySelectorAll('.contact-row').forEach(r => r.classList.remove('selected'));
  document.querySelector(`.contact-row[data-id="${id}"]`)?.classList.add('selected');

  const contact = state.contacts?.find(c => c.id == id);
  if (!contact) return;

  const detail = document.getElementById('contact-detail-v4');
  if (detail) {
    detail.innerHTML = '<div class="loading-state">Loading…</div>';
    detail.scrollTop = 0;
  }

  try {
    const history = await apiFetch(`/api/contacts/${id}/history`);
    if (detail) {
      detail.innerHTML = renderContactDetail(contact, history);
      detail.scrollTop = 0;
    }
  } catch (err) {
    if (detail) detail.innerHTML = '<div class="error-state">Failed to load contact</div>';
  }
};

window.filterContacts = function(role, btn) {
  document.querySelectorAll('.role-chip').forEach(c => c.classList.remove('active'));
  btn.classList.add('active');
  document.querySelectorAll('.contact-row').forEach(row => {
    row.style.display = (role === 'all' || row.dataset.role === role) ? '' : 'none';
  });
};

window.saveContactNotes = async function(contactId, notes) {
  try {
    await apiFetch(`/api/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify({ notes })
    });
  } catch (err) {
    console.error('Failed to save contact notes:', err);
  }
};
